import { formatMoney, formatPrice, addWorking, removeWorking } from './utils.js';
import { applyShippingProgress } from './shipping-progress.js';

/**
 * Quick Add
 *
 * Custom element wrapping the shared quick-add dialog.
 * Handles two flows:
 *   1. Simple (no variants): posts directly to Cart API, no dialog.
 *   2. Multi-variant: fetches product JSON, populates the dialog with
 *      option pickers, then opens it.
 *
 * Public API:
 *   quickAddDialog.open(product) — populate and show the dialog
 */
class QuickAddDialog extends HTMLElement {
    // Internal DOM refs — assigned in connectedCallback
    #dialog;
    #title;
    #price;
    #image;
    #productOptions;
    #variantInput;
    #form;
    #submitButton;
    #submitButtonText;

    #productCache = {};
    #currentProduct = null;
    #currentCartTotal = 0;

    connectedCallback() {
        // Seed cart total and keep it fresh
        fetch('/cart.js')
            .then(r => r.json())
            .then(cart => { this.#currentCartTotal = cart.total_price; })
            .catch(() => { });

        document.addEventListener('cart:count:updated', e => {
            this.#currentCartTotal = e.detail.total;
        });

        this.#dialog = this.querySelector('#quick-add');
        this.#title = this.querySelector('#quick-add-title');
        this.#price = this.querySelector('#quick-add-price');
        this.#productOptions = this.querySelector('#quick-add-options');
        this.#variantInput = this.querySelector('#quick-add-variant-id');
        this.#form = this.querySelector('#quick-add-form');
        this.#submitButton = this.querySelector('#quick-add-submit');
        this.#submitButtonText = this.querySelector('#quick-add-submit-text');

        // Option change — attached once here instead of inside #buildOptions,
        // preventing a listener leak on repeated opens.
        this.#productOptions.addEventListener('change', () => {
            if (this.#currentProduct) {
                this.#updateVariantState(this.#currentProduct);
                this.#previewShippingProgress();
            }
        });

        // Quantity +/- buttons
        this.#form.addEventListener('click', e => {
            const qtyBtn = e.target.closest('[data-qty-change]');
            if (!qtyBtn) return;
            e.preventDefault();
            const delta = parseInt(qtyBtn.dataset.qtyChange, 10);
            const qtyInput = this.#form.querySelector('.quantity-input__value');
            if (!qtyInput) return;
            qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + delta);
            if (this.#currentProduct) {
                this.#updateVariantState(this.#currentProduct);
                this.#previewShippingProgress();
            }
        });

        // Live quantity typing
        this.#form.addEventListener('input', e => {
            if (!e.target.matches('.quantity-input__value')) return;
            if (this.#currentProduct) {
                this.#updateVariantState(this.#currentProduct);
                this.#previewShippingProgress();
            }
        });

        // Form submit
        this.#form.addEventListener('submit', async e => {
            e.preventDefault();
            const variantId = this.#variantInput.value;
            if (!variantId) return;
            await this.#addToCart(variantId);
            this.#dialog.hidePopover();
        });

        // Clear options when the dialog closes
        this.#dialog.addEventListener('toggle', e => {
            if (e.newState === 'closed') {
                this.#productOptions.innerHTML = '';
                this.#currentProduct = null;
            }
        });

        // Trigger buttons live outside this element — use document delegation
        document.addEventListener('click', async e => {
            // Multi-variant: open dialog
            const variantBtn = e.target.closest('[data-quick-add]');
            if (variantBtn) {
                const handle = variantBtn.dataset.productHandle;
                try {
                    variantBtn.disabled = true;
                    const product = await this.#fetchProduct(handle);
                    this.open(product);
                } catch (err) {
                    console.error(err);
                } finally {
                    variantBtn.disabled = false;
                }
                return;
            }

            // Simple: add directly without opening the dialog
            const simpleBtn = e.target.closest('[data-quick-add-simple]');
            if (simpleBtn) {
                const parent = simpleBtn.closest(simpleBtn.dataset.workingTarget);
                addWorking(parent);
                await this.#addToCart(simpleBtn.dataset.variantId);
                removeWorking(parent);
            }
        });
    }

    // ---------------------------------------------------------------------------
    // Public API
    // ---------------------------------------------------------------------------

    /**
     * Populate and open the quick-add dialog for a product.
     * @param {Object} product - Shopify product JSON object
     */
    open(product) {
        this.#currentProduct = product;
        this.#title.textContent = product.title;
        this.#price.innerHTML = this.#moneyRange(product);

        // Reset quantity to 1 each time the dialog opens
        const qtyInput = this.#form.querySelector('.quantity-input__value');
        if (qtyInput) qtyInput.value = '1';

        this.#buildOptions(product);
        this.#updateVariantState(product);
        this.#previewShippingProgress();
        this.#dialog.showPopover();
    }

    // ---------------------------------------------------------------------------
    // Private methods
    // ---------------------------------------------------------------------------

    /**
     * Return a formatted price string for a product, noting price variance.
     * @param {Object} product - Shopify product JSON object
     * @returns {string}
     */
    #moneyRange(product) {
        if (product.price_varies) {
            return `Starting at ${formatMoney(product.price_min)}`;
        }
        const variant = product.variants.find(v => v.available) || product.variants[0];
        return formatPrice(variant.price, variant.compare_at_price);
    }

    /**
     * Append a Shopify CDN width param to an image URL.
     * @param {string} url
     * @param {number|null} [size] - Pixel width; defaults to the image element's width attribute
     * @returns {string}
     */
    #resizedImageUrl(url, size = null) {
        const width = size ?? this.#image.getAttribute('width');
        const param = url.includes('?') ? '&width=' : '?width=';
        return url + param + width;
    }

    /**
     * Fetch product JSON by handle, with in-memory caching.
     * @param {string} handle - Shopify product handle
     * @returns {Promise<Object>} Shopify product JSON object
     */
    async #fetchProduct(handle) {
        if (this.#productCache[handle]) return this.#productCache[handle];
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error(`Failed to load product: ${handle}`);
        const data = await res.json();
        this.#productCache[handle] = data;
        return data;
    }

    /**
     * Find the variant matching the checked option radios in the dialog.
     * Auto-selects the first available variant if nothing is checked yet.
     * @param {Object} product - Shopify product JSON object
     * @returns {Object|null}
     */
    #getSelectedVariant(product) {
        const options = [...this.#productOptions.querySelectorAll('[data-option-position]')];
        const selectedOptions = options.map(el => {
            const selected = el.querySelector('input[type="radio"]:checked, select');
            return selected ? selected.value : null;
        });

        if (!selectedOptions[0]) {
            const firstAvailableVariant = product.variants.find(variant => variant.available);
            // Select each option that the first available variant represents
            firstAvailableVariant?.options.forEach((option, index) => {
                const optionFieldset = this.#productOptions.querySelector(`[data-option-position="${index + 1}"]`);
                if (optionFieldset) {
                    const input = optionFieldset.querySelector(`input[value="${option}"]`);
                    if (input) input.checked = true;
                }
            });
            return firstAvailableVariant;
        }

        return product.variants.find(variant =>
            variant.options.every((option, index) => option === selectedOptions[index])
        ) || null;
    }

    /** Read the current quantity from the input, defaulting to 1. */
    #getQuantity() {
        return Math.max(1, parseInt(this.#form.querySelector('.quantity-input__value')?.value || 1, 10));
    }

    /** Preview free shipping progress for the current variant + quantity against the live cart total. */
    #previewShippingProgress() {
        const progressEl = this.#form.querySelector('.shipping-progress');
        if (!progressEl) return;
        const variant = this.#getSelectedVariant(this.#currentProduct);
        if (!variant) return;
        const previewTotal = this.#currentCartTotal + variant.price * this.#getQuantity();
        applyShippingProgress(progressEl, previewTotal, true);
    }

    /**
     * Sync the dialog's variant input, price, image, and submit button state.
     * @param {Object} product - Shopify product JSON object
     */
    #updateVariantState(product) {
        const variant = this.#getSelectedVariant(product);
        const qty = this.#getQuantity();
        if (variant) {
            this.#variantInput.value = variant.id;
            this.#submitButton.disabled = !variant.available;
            this.#submitButtonText.innerHTML = variant.available ? 'Add to cart&nbsp;&mdash;&nbsp;' : 'Sold out';

            this.#price.innerHTML = variant.available
                ? formatPrice(variant.price * qty, variant.compare_at_price ? variant.compare_at_price * qty : null)
                : '';
        } else {
            this.#variantInput.value = '';
            this.#submitButton.disabled = true;
            this.#submitButtonText.textContent = 'Unavailable';
            this.#price.textContent = '';
        }
    }

    /**
     * Build option picker fieldsets in the dialog from product JSON.
     * @param {Object} product - Shopify product JSON object
     */
    #buildOptions(product) {
        this.#productOptions.innerHTML = '';
        product.options.forEach(option => {
            const fieldset = document.createElement('fieldset');
            fieldset.className = 'option-group';
            fieldset.dataset.optionPosition = option.position;

            const legend = document.createElement('legend');
            legend.className = 'visually-hidden';
            legend.textContent = option.name;
            fieldset.appendChild(legend);

            const values = document.createElement('div');
            values.className = 'option-inputs';

            option.values.forEach((value, i) => {
                const id = `quick-add-option-${option.position}-${i}`;
                const variantWithImage = product.variants.find(
                    variant => variant.options[option.position - 1] === value && variant.featured_image
                );

                const input = document.createElement('input');
                input.type = 'radio';
                input.name = `option-${option.position}`;
                input.id = id;
                input.value = value;
                input.className = 'option-input visually-hidden';

                const optionName = document.createElement('span');
                optionName.className = 'option-label__name';
                optionName.textContent = value;

                const optionDetail = document.createElement('span');
                optionDetail.className = 'option-label__detail';
                optionDetail.textContent = window.optionLabels?.[product.variants.find(
                    variant => variant.options[option.position - 1] === value
                )?.id] || '';

                // Disable option if no variant with this value is available
                const isOptionAvailable = product.variants.some(
                    variant => variant.options[option.position - 1] === value && variant.available
                );
                if (!isOptionAvailable) {
                    input.disabled = true;
                    optionDetail.textContent = 'Out of stock';
                }

                const label = document.createElement('label');
                label.htmlFor = id;
                label.className = 'option-label';

                if (variantWithImage?.featured_image) {
                    const optionImage = document.createElement('img');
                    optionImage.className = 'option-label__image';
                    optionImage.alt = '';
                    optionImage.width = 60;
                    optionImage.height = 60;
                    optionImage.src = this.#resizedImageUrl(variantWithImage.featured_image.src, 60);
                    label.appendChild(optionImage);
                }
                label.appendChild(optionName);
                label.appendChild(optionDetail);

                values.appendChild(input);
                values.appendChild(label);
            });

            fieldset.appendChild(values);
            this.#productOptions.appendChild(fieldset);
        });
    }

    /**
     * POST a variant to the Cart API and fire cart update events.
     * @param {number|string} variantId - Shopify variant ID
     */
    async #addToCart(variantId) {
        addWorking(this.#dialog);
        try {
            const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: variantId, quantity: this.#getQuantity() }),
            });
            if (!res.ok) throw new Error('Cart add failed');
            const cartRes = await fetch('/cart.js');
            const cart = await cartRes.json();
            document.dispatchEvent(new CustomEvent('cart:count:updated', {
                detail: { count: cart.item_count, total: cart.total_price }
            }));
            document.dispatchEvent(new CustomEvent('cart:item:added'));
        } catch (err) {
            console.error(err);
        } finally {
            removeWorking(this.#dialog);
        }
    }
}

customElements.define('quick-add-dialog', QuickAddDialog);

